import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { GetTokens } from '../../../../../core/shared/actions/token.actions';
import { LoadingSpinnerComponent } from '../../../../../core/shared/components/loading-spinner/loading-spinner.component';
import { Token } from '../../../../../core/shared/models/token.model';
import { TokenState } from '../../../../../core/shared/states/token.state';
import { AdminTokenModalComponent } from '../../modals/admin-token-modal/admin-token-modal.component';

@Component({
  selector: 'app-admin-tokens-list',
  standalone: true,
  templateUrl: './admin-tokens-list.component.html',
  styleUrl: './admin-tokens-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    LoadingSpinnerComponent,
    MatInputModule,
    MatIcon,
    MatIconButton,
  ],
})
export class AdminTokensListComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  public dataSource = new MatTableDataSource<Token>();
  public displayedColumns = ['icon', 'Token', 'PK', 'CreationDate', 'Enabled', 'actions'];
  public tokenCount = 0;
  public activeTokens = 0;
  public disabledTokens = 0;
  public loading = true;
  public paginator!: MatPaginator;
  public sort!: MatSort;
  public readonly tokens$: Observable<Token[]>;

  constructor(
    private readonly store: Store,
    private readonly modalService: NgbModal,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.store.dispatch(new GetTokens());
    this.tokens$ = this.store.select(TokenState.getTokenList);
  }

  ngOnInit() {
    this.tokens$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.dataSource.data = data;
        this.tokenCount = data.length;
        this.activeTokens = data.filter((token) => token.Enabled).length;
        this.disabledTokens = this.tokenCount - this.activeTokens;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  /** Converts day-first dates with dot or slash separators into a Date the DatePipe can consume. */
  parseCreationDate(value: string): Date | null {
    if (!value) {
      return null;
    }

    const match = /^(\d{1,2})[./](\d{1,2})[./](\d{4}),?\s*(\d{2}):(\d{2}):(\d{2})$/.exec(value.trim());
    if (!match) {
      return null;
    }

    const [, day, month, year, hours, minutes, seconds] = match;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds)
    );
  }

  openModal(selectedToken: Token, formMode: string, editToken: boolean) {
    const modalRef = this.modalService.open(AdminTokenModalComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.token = selectedToken;
    modalRef.componentInstance.editToken = editToken;
    modalRef.componentInstance.formMode = formMode;
  }

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    this.setDataSourceAttributes();
  }

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }

  setDataSourceAttributes() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }
}
